import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '@/components/layout/PublicLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Star, Loader2, Building2, FileText, Briefcase } from 'lucide-react';
import { pricingAPI } from '@/services/api';

export default function PricingPage() {
  const navigate = useNavigate();
  const [individualPlans, setIndividualPlans] = useState([]);
  const [businessPlans, setBusinessPlans] = useState([]);
  const [llcPlans, setLlcPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fallback plans in case API fails or returns empty
  const fallbackIndividualPlans = [
    { name: 'Basic', price: 99, features: ['Form 1040', 'Standard deduction', 'E-filing included', 'Email support'], popular: false },
    { name: 'Standard', price: 199, features: ['All Basic features', 'Itemized deductions', 'Investment income', 'State return', 'Priority support'], popular: true },
    { name: 'Premium', price: 349, features: ['All Standard features', 'Rental property', 'Foreign income', 'FBAR/FATCA', 'Phone support', 'Tax planning'], popular: false }
  ];

  const fallbackBusinessPlans = [
    { name: 'LLC/Partnership', price: 599, features: ['Schedule C', 'Form 1065', 'K-1 preparation', 'E-filing'], popular: false },
    { name: 'S-Corp', price: 899, features: ['Form 1120S', 'K-1s', 'Payroll coordination', 'Quarterly estimates'], popular: true },
    { name: 'C-Corp', price: 1299, features: ['Form 1120', 'Complex deductions', 'Multi-state filing', 'Advisory included'], popular: false }
  ];

  const fallbackLlcPlans = [
    { name: 'Basic', price: 99, features: ['State filing', 'Operating agreement', 'EIN application', 'Digital documents'], popular: false, turnaround: '7-10 business days' },
    { name: 'Standard', price: 249, features: ['All Basic features', 'Registered agent (1 year)', 'Banking resolution', 'Priority processing'], popular: true, turnaround: '3-5 business days' },
    { name: 'Premium', price: 399, features: ['All Standard features', 'Compliance calendar', 'Annual report filing', 'Dedicated support'], popular: false, turnaround: '1-2 business days' }
  ];

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        
        // Fetch individual tax plans (plan_type='tax', is_business_filing=false)
        const individual = await pricingAPI.getPlans('tax', true, false);
        
        // Fetch business tax plans (plan_type='tax', is_business_filing=true)
        const business = await pricingAPI.getPlans('tax', true, true);
        
        // Fetch LLC formation plans (plan_type='llc') - don't filter by is_business_filing
        const llcResponse = await pricingAPI.getPlans('llc', true);

        console.log('Individual plans:', individual);
        console.log('Business plans:', business);
        console.log('LLC plans:', llcResponse);

        setIndividualPlans(individual.length > 0 ? individual : fallbackIndividualPlans);
        setBusinessPlans(business.length > 0 ? business : fallbackBusinessPlans);
        setLlcPlans(llcResponse.length > 0 ? llcResponse : fallbackLlcPlans);
      } catch (error) {
        console.error('Error fetching pricing plans:', error);
        // Use fallback plans
        setIndividualPlans(fallbackIndividualPlans);
        setBusinessPlans(fallbackBusinessPlans);
        setLlcPlans(fallbackLlcPlans);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const formatPrice = (price) => {
    return (
      <>
        ${Number(price).toLocaleString()}<sup className="text-xs align-super">*</sup>
      </>
    );
  };

  const PriceDisclaimer = () => (
    <p className="text-center text-sm text-muted-foreground mt-6">
      *Final price based on your tax situation
    </p>
  );

  const PlanCard = ({ plan, index, colorTheme = 'blue', showTurnaround = false }) => {
    const themeColors = {
      blue: { border: 'border-blue-500', text: 'text-blue-600', badge: 'bg-blue-500' },
      green: { border: 'border-green-500', text: 'text-green-600', badge: 'bg-green-500' },
      purple: { border: 'border-purple-500', text: 'text-purple-600', badge: 'bg-purple-500' }
    };
    const colors = themeColors[colorTheme];

    return (
      <Card className={`relative ${plan.popular ? `${colors.border} border-2 shadow-lg` : ''}`}>
        {plan.popular && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <Badge className={`${colors.badge} text-white flex items-center gap-1`}>
              <Star className="h-3 w-3 fill-white" /> Most Popular
            </Badge>
          </div>
        )}
        <CardHeader className="pt-8">
          <CardTitle className="text-2xl">{plan.name}</CardTitle>
          {plan.description && (
            <p className="text-sm text-muted-foreground">{plan.description}</p>
          )}
          <div className={`text-3xl font-bold ${colors.text} mt-2`}>{formatPrice(plan.price)}</div>
          {showTurnaround && plan.turnaround && (
            <p className="text-sm text-muted-foreground mt-1">{plan.turnaround}</p>
          )}
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 mb-6">
            {(plan.features || []).map((feature, idx) => (
              <li key={idx} className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{feature}</span>
              </li>
            ))}
          </ul>
          <Button className="w-full" onClick={() => navigate('/login')}>Get Started</Button>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="container mx-auto px-4 py-12 flex items-center justify-center min-h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold mb-4">Pricing</h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Transparent pricing for all our services
          </p>
        </div>

        {/* Individual Tax Filing */}
        <section className="mb-16">
          <div className="flex items-center justify-center gap-3 mb-8">
            <FileText className="h-8 w-8 text-blue-600" />
            <h2 className="text-3xl font-bold text-center">Individual Tax Filing</h2>
          </div>
          <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
            Personal tax returns for individuals, including Form 1040 and state filings
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {individualPlans.map((plan, index) => (
              <PlanCard key={plan.id || index} plan={plan} index={index} colorTheme="blue" />
            ))}
          </div>
          <PriceDisclaimer />
        </section>

        {/* Business Tax Filing */}
        <section className="mb-16 bg-gray-50 dark:bg-gray-900/50 -mx-4 px-4 py-12 rounded-lg">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Briefcase className="h-8 w-8 text-green-600" />
            <h2 className="text-3xl font-bold text-center">Business Tax Filing</h2>
          </div>
          <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
            Tax returns for LLCs, S-Corps, C-Corps, and partnerships
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {businessPlans.map((plan, index) => (
              <PlanCard key={plan.id || index} plan={plan} index={index} colorTheme="green" />
            ))}
          </div>
          <PriceDisclaimer />
        </section>

        {/* LLC Formation */}
        <section className="mb-16">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Building2 className="h-8 w-8 text-purple-600" />
            <h2 className="text-3xl font-bold text-center">LLC Formation</h2>
          </div>
          <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
            Start your business with professional LLC formation services
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {llcPlans.map((plan, index) => (
              <PlanCard key={plan.id || index} plan={plan} index={index} colorTheme="purple" showTurnaround={true} />
            ))}
          </div>
          <PriceDisclaimer />
        </section>

        {/* Additional Services */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Additional Services</h2>
          <div className="max-w-md mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>Accounting & Payroll</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm mb-4">
                  <li>Monthly Bookkeeping</li>
                  <li>Payroll Services</li>
                  <li>CFO Advisory</li>
                </ul>
                <Button variant="outline" className="w-full" onClick={() => navigate('/contact')}>Get Quote</Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center bg-blue-50 dark:bg-blue-900/20 rounded-lg p-12">
          <h2 className="text-3xl font-bold mb-4">Need a Custom Quote?</h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Contact us for personalized pricing based on your specific needs.
          </p>
          <Button size="lg" onClick={() => navigate('/contact')}>Contact Sales</Button>
        </div>
      </div>
    </PublicLayout>
  );
}
